import React, { useState, useEffect } from 'react';
import {ChartNoAxesGantt, Ruler, Scale, Banknote, BanknoteArrowDown, RulerDimensionLine } from 'lucide-react';
import {  CircularProgress, Box } from '@mui/material';
import { IconUserCode } from '@tabler/icons-react';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf'; 

import LeaveImpactRulesManager from './LeaveImpact';
import LeaveBalanceManager from './LeaveBalance';
import { getAllLeaveBalance } from '../../services/leaveBalanceAPIs';
import PayrollImpactDashboard from './PayrollImpactDashboard';
import SalaryBreakdown from './SalaryBreakdown';
import PageHeader from '../../components/headers/Header';
import AbsenceImpactRulesManager from './AbsenceImpact';

import { exportToPdf } from '../../utils/exportToPdf'; 

const NotEmployee = localStorage.getItem('userRole')!=='EMPLOYEE';

const RulesManagement = () => {
  const [IsEmployee,setIsEmployee]=useState(localStorage.getItem('userRole')==='EMPLOYEE' || localStorage.getItem('userRole')==='DEV');
    
  const [employees, setEmployees] = useState([]);
  const [currentDate] = useState(new Date('2025-06-19T10:26:00+01:00'));
  const [activeTab, setActiveTab] = useState('leaveBalance');

  // PDF states
  const [isExporting, setIsExporting] = useState(false);
  const [exportError, setExportError] = useState(null);
  const [snackbarOpen, setSnackbarOpen] = useState(false);

  const handleExportPdf = async () => {
    setIsExporting(true);
    setExportError(null);
        
    let elementId = '';
    let options = {
      filename: `absence-${activeTab}.pdf`,
      ignoreElements: ['.page-header', '.Leaves-nav-tabs', '.MuiModal-root'],
      scale: 1.5,
      margin: 10,
      isCalendar: activeTab === 'calendar',
      onStart: () => setIsExporting(true),
      onComplete: () => setIsExporting(false),
      onError: (message) => {
        setExportError(message);
        setSnackbarOpen(true);
      },
      autoHeight: true,
    };
    
    switch(activeTab) {
      case 'overview':
        elementId = 'leave-dashboard-container';
        break;
      case 'leaves':
        elementId = 'leaves-container';
        break;
      case 'calendar':
        elementId = 'leave-calendar-container';
        options.scale = 1.2;
        break;
      case 'add':
        elementId = 'add-leave-container';
        break;
      default:
        elementId = 'leave-dashboard-container';
    }
    await exportToPdf(elementId, options);
  };
  
  
  useEffect(() => {
    const fetchData = async () => {
      try {
        const data = await getAllLeaveBalance();
        setEmployees(data);
  
      } catch (error) {
        console.error("Error fetching data:", error);
      }
    };
    fetchData();
  }, []);
  
  // Handling active tab 
  useEffect(() => {
      if (IsEmployee ) {
        setActiveTab('leaveBalance'); // or 'absences', depending on your desired default
      } else if (!IsEmployee && activeTab === 'salaryBreakdown') {
        setActiveTab('leaveImpactRules'); // Set to 'overview' when switching to non-employee
      }
    }, [IsEmployee]);


  return (
    <div className="Leaves-main-container">
      <div className="Leaves-content-wrapper">
        <PageHeader 
          // title={!IsEmployee ? "Rules Management" : "Leave Balance & Salary Breakdown"} 
          title={!IsEmployee ? "Rules Management" : "Leave Balance"} 
          //subtitle={!IsEmployee ? "Define leave & absence impact on payroll" : "Track your leave balance and salary breakdown"}
          subtitle={!IsEmployee ? "Define leave & absence impact on payroll" : "Track your leave balance change"}
         actions={
            <Box sx={{ position: 'relative', display:'flex', gap:'10px' }}>
              <button 
                onClick={handleExportPdf}
                disabled={isExporting}
                style={{
                  padding: '8px 16px',
                  backgroundColor: 'transparent',
                  color: '#3d52a0',
                  border: '1px solid #3d52a0',
                  borderRadius: '8px',
                  fontSize: '14px',
                  fontWeight: '500',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  opacity: isExporting ? 0.7 : 1
                }}
              >
                {isExporting ? (
                  <>
                    <CircularProgress size={20} thickness={4} sx={{ color: '#3d52a0', mr: 1 }} />
                    Exporting...
                  </>
                ) : (
                  <>
                    <PictureAsPdfIcon fontSize="small" sx={{ mr: 1 }} />
                    Export
                  </>
                )}
              </button>
                {(NotEmployee &&
                  <button
                    onClick={() => setIsEmployee(!IsEmployee)}
                    // disabled={isExporting}
                    style={{
                      padding: '8px 16px',
                      backgroundColor: 'transparent',
                      color: '#3d52a0',
                      border: '1px solid #3d52a0',
                      borderRadius: '8px',
                      fontSize: '14px',
                      fontWeight: '500',
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      opacity: isExporting ? 0.7 : 1
                    }}
                  >
                    <IconUserCode style={{ color: '#3d52a0', margin: 'auto', marginRight: '6px' }} />
                    {IsEmployee ? 'General Portal' : 'Personal Portal'}
                  </button>        
                )}
              </Box>
            }
          />
        <div className="Leaves-nav-tabs">
          <div className="Leaves-tab-bar">
            {[
              ...(!IsEmployee ? [{ id: 'leaveImpactRules', name: 'Leave Impact Rules', icon: Ruler  }] : []),
              ...(!IsEmployee ? [{ id: 'absenceImpactRules', name: 'Absence Impact Rules', icon: RulerDimensionLine }]: []),
              { id: 'leaveBalance', name: 'Leave Balance', icon: Scale },
              ...(!IsEmployee ? [{ id: 'payrollImpact', name: 'Monthly Payroll', icon: Banknote }]: []),
              // { id: 'salaryBreakdown', name: 'Salary Breakdown', icon: BanknoteArrowDown },
            ].map(tab => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`Leaves-tab-btn ${activeTab === tab.id ? 'Leaves-tab-btn-active' : ''}`}
              >
                <tab.icon className="Leaves-tab-icon" />
                {tab.name}
              </button>
            ))}
          </div>
        </div>
        {!IsEmployee && activeTab === 'leaveImpactRules' && (
          <LeaveImpactRulesManager/>
        )}
        {!IsEmployee && activeTab === 'absenceImpactRules' && (
          <AbsenceImpactRulesManager/>
        )}
        {activeTab === 'leaveBalance' && (
          <LeaveBalanceManager employees={employees} IsEmployee={IsEmployee} />
        )}
        {activeTab === 'salaryBreakdown' && (
          <SalaryBreakdown isEmployee={IsEmployee} />
        )}
        {!IsEmployee && activeTab === 'payrollImpact' && (
          <PayrollImpactDashboard/>
        )}
      </div>
    </div>
  );
};

export default RulesManagement;